import { RoleEnum } from '@anecdotes/auth-core';
import { addMappings } from '@anecdotes/feature-flags';

const PermissionsMapping = {
  /** Dashboard page permissions */
  '@dashboard-page': ['dashboard:list'],
  '@dashboard-write': ['dashboard:write'],

  /** Requirement hub page permissions */
  '@requirement-hub-page': [
    'framework:read and framework:write',
    RoleEnum.Admin,
    RoleEnum.Collaborator,
    RoleEnum.Manager,
    'requirement:*:read'
  ],

  '@contributor-or-viewer': [RoleEnum.Contributor, RoleEnum.Viewer],
  '@contributor': [RoleEnum.Contributor],
  '@viewer': [RoleEnum.Viewer],
  '@auditor': [RoleEnum.Auditor],

  /** Unified view page permissions */
  '@unified-view-page': ['framework:read and framework:write', RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager],

  /** Evidence general permissions */
  '@evidence-new-version-button': ['evidence:write'],
  '@evidence-recollect-button': ['service:invoke'],
  /** Evidence pool page permissions */
  '@evidence-pool-page': [`evidence:read and evidence:write and not (${RoleEnum.Contributor} or ${RoleEnum.Viewer})`, RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager],
  '@evidence-pool-link-evidence': ['framework:*:read', 'framework:read'],
  '@edit-evidence': ['evidence:update'],
  '@evidence-delete': ['evidence:delete and risk:read and policy:read and uar:read'],

  /** Evidence lab page permissions */
  '@evidence-lab-page': ['ticketevidence:write', RoleEnum.Admin],

  /** Frameworks page permissions */
  '@frameworks-page': ['framework:read', 'framework:*:read', RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager, RoleEnum.Auditor, RoleEnum.Contributor, RoleEnum.Viewer],
  '@frameworks-folders-view': [RoleEnum.Admin, RoleEnum.Manager, `(framework:read or framework:*:read) and not ${RoleEnum.Auditor}`, RoleEnum.Contributor, RoleEnum.Viewer],
  '@frameworks-folders-actions': [RoleEnum.Admin, RoleEnum.Manager],
  '@frameworks-library': [`not (${RoleEnum.Contributor} or ${RoleEnum.Viewer})`],
  '@frameworks-all': ['framework:read'],
  '@frameworks-not-adopted': ['framework:update', 'framework:*:update'],
  '@frameworks-create': ['framework:write'],
  '@framework-order-change': ['frameworkorder:update'],
  '@framework-item-menu-manage-plugins': ['framework:update', 'framework:{{frameworkId}}:update'],
  '@framework-item-menu-edit': ['framework:update', 'framework:{{frameworkId}}:update'],
  '@framework-item-menu-duplicate': ['framework:write'],
  '@framework-item-menu-abandon': ['framework:write', 'framework:{{frameworkId}}:write'],
  '@framework-item-menu-delete': ['framework:delete', 'framework:{{frameworkId}}:delete'],
  '@auditor-simulation': ['auditor:simulate'],

  /** Framework overview page permissions */
  '@framework-specific': ['framework:{{frameworkId}}:read', 'framework:read'],
  '@framework-automate-control-statuses': [RoleEnum.Admin, RoleEnum.Manager],
  '@framework-adopt': ['framework:{{frameworkId}}:update', 'framework:update'],
  '@framework-settings-page': ['framework:update', 'framework:{{frameworkId}}:update'],
  '@frameworks-settings-allow-auditor-download': [RoleEnum.Admin, RoleEnum.Manager],
  '@frameworks-settings-allow-auditor-upload-files': [RoleEnum.Admin, RoleEnum.Manager],
  '@allow-auditor-view-custom-field': [RoleEnum.Admin, RoleEnum.Manager],
  '@framework-audit-history-page': ['audit:read and audit:write'],
  '@framework-edit-audit': ['(framework:update or framework:{{frameworkId}}:update) and audit:update'],
  '@framework-reset-audit': ['(framework:update or framework:{{frameworkId}}:update) and audit:delete'],
  '@framework-setup-audit': ['(framework:update or framework:{{frameworkId}}:update) and audit:write'],
  '@framework-report': ['framework:{{frameworkId}}:update', 'framework:update'],
  '@framework-item-comments-action': [],
  '@framework-item-tasks-action': [`not ${RoleEnum.Auditor}`],
  '@framework-item-collected-evidence-action': [`not ${RoleEnum.Auditor}`],
  '@framework-item-evidence-status-action': [`not ${RoleEnum.Auditor}`],
  '@framework-item-reminders-action': [`not ${RoleEnum.Auditor}`],
  '@framework-configure-evidence-visibility': ['audit:update'],
  '@framework-overview-change-categories': ['framework:update', 'framework:*:update'],
  '@audit-evidence-status-configuration': ['framework:update', 'framework:*:update'],

  /** Controls page permissions */
  '@control-specific': ['control:{{controlId}}:read', 'control:read'],
  '@control-add': ['control:write'],
  '@control-edit': [`control:update and not ${RoleEnum.Auditor}`, RoleEnum.Contributor],
  '@control-remove': ['control:delete'],
  '@control-generate-report': [RoleEnum.Admin, RoleEnum.Manager, RoleEnum.Contributor, 'customRole'],
  '@control-activity-log': [RoleEnum.Admin, RoleEnum.Manager, RoleEnum.Contributor, 'customRole'],
  '@control-owner': [RoleEnum.Admin, RoleEnum.Manager, RoleEnum.Contributor, RoleEnum.Viewer, 'customRole'],
  '@control-reminders': [`reminder:write and reminder:read`, RoleEnum.Viewer],
  '@control-reminders-actions': [`reminder:write and reminder:read`],
  '@control-tasks': ['collaboration:read and collaboration:write', RoleEnum.Viewer],
  '@control-openai-section': [`not ${RoleEnum.Auditor} and not ${RoleEnum.Viewer}`],
  '@control-risk-list-section': [`not (${RoleEnum.Auditor} or ${RoleEnum.Contributor} or ${RoleEnum.Viewer})`],
  '@control-risk-linking': ['risk:write'],
  '@control-tags': [RoleEnum.Admin, RoleEnum.Manager, 'customRole'],
  '@control-maturity-level': [RoleEnum.Admin, RoleEnum.Manager, 'customRole'],
  '@control-bulk-actions': [`not ${RoleEnum.Auditor}`],
  '@control-set-in-progress-status': [RoleEnum.Admin, RoleEnum.Manager, 'customRole'],
  '@control-set-gap-status': [`not ${RoleEnum.Auditor}`],
  '@control-set-monitoring-status': [RoleEnum.Admin, RoleEnum.Manager, 'customRole'],
  '@control-set-not-started-status': [`not ${RoleEnum.Auditor}`],
  '@control-set-not-applicable-status': [`not ${RoleEnum.Auditor}`],
  '@control-set-ready-for-audit-status': [`not ${RoleEnum.Auditor}`],
  '@control-set-issue-status': [RoleEnum.Auditor],
  '@control-set-approved-by-auditor-status': [RoleEnum.Auditor],
  '@control-requirement-ticketing': [`not ${RoleEnum.Auditor}`],
  '@control-custom-fields': ['framework:write and framework:update and not'],
  '@custom-field-edit-only-admin': [RoleEnum.Admin],
  '@custom-field-edit-internal-users': [`not ${RoleEnum.Auditor} and control:write or risk:write`],
  '@control-covert-attachment-to-evidence': [RoleEnum.Admin, RoleEnum.Manager, `not ${RoleEnum.Auditor} and control:write`],
  '@users-with-control-access': [`control:read and not (${RoleEnum.Auditor} or ${RoleEnum.Viewer})`, RoleEnum.Contributor],
  '@share-control': ['control:read or control:*:read'],
  '@invite-user-and-share-control': [`control:read and user:write and ${RoleEnum.Admin}`],

  '@requirement-collect-from-evidence-pool': ['requirement:update and evidence:write'],
  '@requirement-attach-policy': ['requirement:update and evidence:write and service:read'],
  '@requirement-attach-url': ['requirement:update and evidence:write and service:read'],
  '@requirement-upload-file': ['requirement:update and evidence:write and service:read'],
  '@requirement-collect-plugin-evidence': ['requirement:update and evidence:write and service:read'],
  '@requirement-edit': ['requirement:update'],
  '@requirement-detach': [RoleEnum.Admin, RoleEnum.Manager, 'customRole'], // TODO: there is no a specific permission that would allow/disallow this so far.
  '@requirement-remove': ['requirement:delete'],
  '@requirement-add-from-library': [`(control:update or control:*:update) and requirement:read`, RoleEnum.Contributor],
  '@requirement-add-custom': [`control:update and requirement:write`],
  '@requirement-link-to-control': [`control:update`],
  '@requirement-frequency-reminder': [`not ${RoleEnum.Auditor}`],
  '@requirement-owner': [`not ${RoleEnum.Auditor}`],
  '@requirement-evidence-suggestion': [`not ${RoleEnum.Auditor}`],
  '@evidence-state-indicator': [`not ${RoleEnum.Auditor}`],
  '@requirement-ask-for-help': [`not (${RoleEnum.Contributor} or ${RoleEnum.Viewer})`],
  '@ai-recommendations': [`not (${RoleEnum.Auditor} or ${RoleEnum.Viewer})`],
  '@crud-custom-fields': [RoleEnum.Admin, RoleEnum.Manager],
  '@edit-custom-field-value': ['control:write or framework:write or framework:{{frameworkId}}:update or risk:write'],
  '@users-with-requirement-access': [`requirement:read and not (${RoleEnum.Auditor} or ${RoleEnum.Viewer})`, RoleEnum.Contributor],
  '@share-requirement': ['requirement:read'],
  '@invite-user-and-share-requirement': [`requirement:read and user:write and ${RoleEnum.Admin}`],

  /** Risk page permissions */
  '@risk-page': ['risk:read', 'risk:*:read', 'risk:own', RoleEnum.Contributor, RoleEnum.Viewer],
  '@risk-readonly-access': ['(risk:read or risk:*:read) and not (risk:write or risk:*:write)'],
  '@risk-dashboard': ['risk:read'],
  '@risk-empty-state-tip': [],
  '@risk-tickets-button': ['collaboration:read', RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager],
  '@risk-activity-log': [],
  '@risk-resend-for-review': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-settings': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-add-risks': ['risk:write'],
  '@risk-export-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-tasks': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-manager-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-item-menu': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-id-edit': ['risk:write'],
  '@risk-name-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-link-controls-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-add-documents-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-activity-log-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-owner-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-category-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-source-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-effect-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-tags-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-creation-time-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-description-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-threat-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-vulnerability-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-inherent-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-residual-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-target-level-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-add-treatment-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-treatment-status-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-treatment-expires-date-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-treatment-method-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-approval-process-settings-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-resend-approval-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-treatment-history-delete-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-view-full-control-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-linked-control-menu': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-add-task-button': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-document-name-edit': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-document-copy-name': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-document-menu': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-edit-custom-fields': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-level-automation': ['risk:write', 'risk:*:write', 'risk:own'],
  '@risk-bulk-actions': ['risk:write', 'risk:*:write', 'risk:own'],
  '@invite-user-and-share-risk': [`risk:read and user:write and ${RoleEnum.Admin}`],
  '@share-risk': ['risk:read', 'risk:*:read'],
  '@risk-share-action': ['risk:write', 'risk:*:write', 'risk:own'],

  /** Policy manager page permissions */
  '@policy-page': ['policy:read and policy:write', RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager],
  '@policy-resend-for-review': ['policy:update'],
  '@policy-ai': [RoleEnum.Admin, RoleEnum.Manager],
  '@policy-ai-consent': [RoleEnum.Admin, RoleEnum.Manager],

  /** Settings page permissions */
  '@settings-page': ['customer:update'],
  '@login-methods-page': ['customer:update'],
  '@login-methods-idp-email-support-user': ['loginsettings:update'],
  '@scim-page': ['customer:update'],
  '@data-delegation-page': ['customer:update'],
  '@connected-apps-page': ['customer:update'],

  /** UAR page permissions */
  '@uar-page': ['uar:read and uar:write', RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager],
  '@uar-monitoring-page': [],
  '@uar-my-reviews-page': [],
  '@uar-employees-list-page': ['employeeslist:list and employeeslist:write', RoleEnum.Admin],
  '@uar-tickets-button': ['collaboration:read', RoleEnum.Admin, RoleEnum.Collaborator, RoleEnum.Manager],
  '@uar-resend-for-review': ['uar:write'],

  /** Playbook page permissions */
  '@playbooks-page': [
    'playbook:read and playbook:write',
    RoleEnum.Admin,
    RoleEnum.Collaborator,
    RoleEnum.Manager,
    RoleEnum.Manager,
  ],

  /** Plugins page permissions */
  '@plugins-page': ['service:read and service:write', RoleEnum.Admin, RoleEnum.It],

  /** Trust Center page permissions */
  '@trust-center': [RoleEnum.Admin, RoleEnum.TrustCenterAccessManager, 'trustcenteraccessmanager:read'],
  '@trust-center-tab': [RoleEnum.Admin, RoleEnum.TrustCenterAccessManager, 'trustcenteraccessmanager:read'],
  '@trust-center-load-data': ['trustcentermanagement:read'],
  '@trust-center-preview-page': ['trustcentermanagement:read'],
  '@trust-center-settings-page': ['trustcentermanagement:read'],

  /** My anecdotes page permissions */
  '@my-anecdotes-page': [`collaboration:read and not (${RoleEnum.Viewer})`],
  '@my-anecdotes-create-task': ['collaboration:write'],
  // TODO: As of now we don't have specific scopes for full access. This feature flag
  // is used in order to display only tasks that the user is assigned to in case the user
  // is not Admin or Manager
  '@my-anecdotes-not-assigned-to-current-user-tasks': ['collaboration:admin'],
  '@my-anecdotes-email-collaboration-access': ['collaboration:admin'],
  '@my-anecdotes-slack-collaboration-access': ['collaboration:admin'],
  '@my-anecdotes-collaboration-change-status': ['collaboration:update'],
  '@my-anecdotes-collaboration-edit': ['collaboration:update'],
  '@my-anecdotes-collaboration-ping-task-owner': ['collaboration:write'],
  '@my-anecdotes-collaboration-delete': ['collaboration:delete'],

  /** Access management page */
  '@access-management-page': [RoleEnum.Admin],

  // Users page
  '@users-page': ['user:read and user:write'],
  '@users-change-role': ['user:update'],

  '@api-tokens-page': ['apitoken:read and apitoken:write'],
  '@audit-logs-page': [],

  // Roles page
  '@roles-page': ['role:read'],
  '@role-create': ['role:write'],
  '@role-edit': ['role:update'],
  '@role-delete': ['role:delete'],

  /** General permissions */
  '@invite-user': ['user:write', RoleEnum.Admin],
  '@notifications-bell': [
    'notifications:read and notifications:write',
    RoleEnum.Admin,
    RoleEnum.Collaborator,
    RoleEnum.Manager,
    RoleEnum.Auditor,
    RoleEnum.It,
  ],
  '@auditors-portal-page': [RoleEnum.Auditor],
  '@anecdotes-help-link': [],
  '@status-page': [],
  '@release-notes': [],

  /** Collaboration */
  '@create-tickets-btn': ['collaboration:write'],
  '@collaboration-can-review-ticket-attachments': [`collaboration:update and not (${RoleEnum.Viewer})`],

  /** Commenting */
  '@commenting-full-access': [RoleEnum.Admin],

  /** Evidence item show linked controls label permission */
  '@evidence-item-show-linked-controls': ['framework:list and control:list and requirement:list and evidence:list'],

  /** Analysis rules */
  '@analysis-rules-add': ['analysisrules:write'],

  /** Auditors Portal */
  '@auditors-portal': [RoleEnum.Zone],
};

const CustomerFeaturesMapping = {
  // Stigg features
  'feature-scim': ['@scim-page'],
  'feature-uar-entities': ['@uar-add-new-entities'],
  'feature-customRoles': ['@role-create', '@role-edit', '@role-delete'],
  'feature-risks-entities': ['@risk-add-new-entities'],
  'feature-risk-dashboard-mgmt-overview': ['@risk-dashboard-mgmt-overview-page'],
  'feature-sigma-premium': ['@sigma-premium'],
  'feature-policy-guardian': ['@policy-ai'],
  'feature-compliance-product-evidence-bulk-actions': ['@evidence-bulk-actions'],
  'feature-compliance-product-requirement-hub-link-modal': ['@requirement-hub-link-modal'],
  'feature-compliance-product-requirement-hub-widgets': ['@requirement-hub-widgets'],
  'feature-control-ai-assistant': ['@feature-control-ai-assistant'],
  'feature-control-policy-statements': ['@feature-control-policy-statements'],
  'feature-control-insights': ['@control-insights'],
  'feature-compliance-product-new-chatgrc': ['@feature-chatgrc'],
  'feature-evidence-views-p-2': ['@feature-evidence-views-p2'],
  'feature-merged-evidence': ['@feature-merged-evidence'],
  'feature-nql-ai': ['@feature-nql-ai'],
  'feature-document-ai-analysis': ['@feature-document-ai-analysis'],
  'feature-smart-search-ai': ['@feature-smart-search-ai'],
  'feature-evidence-builder': ['@feature-evidence-builder'],
  'feature-evidence-builder-ai-suggestions': ['@feature-evidence-builder-ai-suggestions'],
  
  // Account features coming from customer endpoint
  'AI Consent': ['@ai-consent'],
  'Policy AI': ['@policy-ai-consent'],
  'Audit Logs': ['@audit-logs-page'],
  'Tasks and Reviews': ['@my-anecdotes-tasks-and-reviews'],
  'Trust Center': ['@trust-center-load-data', '@trust-center-preview-page', '@trust-center-settings-page', '@trust-center-tab'],
  'My subscription': ['@my-subscription-page'],
  'Iceberg Evidence': ['@evidence-iceberg-support'],
  'Not Data Engine': [
    '@frameworks-page',
    '@unified-view-page',
    '@requirement-hub-page',
    '@risk-page',
    '@policy-page',
    '@uar-page',
    '@trust-center',
    '@my-anecdotes-page',
    '@dashboard-compliance-page',
    '@evidence-pool-link-evidence',
    '@grc-app-subscription-group',
    '@subscription-usage-features'
  ],
};

addMappings({
  permissionMappings: PermissionsMapping,
  customerFeaturesMapping: CustomerFeaturesMapping,
});
